import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  Alert,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import { LocationOn, CheckCircle, Error as ErrorIcon } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useApiClient } from '../utils/apiClient';

const AddressValidation = () => {
  const { getAuthToken } = useAuth();
  const apiClient = useApiClient();
  const [formData, setFormData] = useState({
    name: '',
    address1: '',
    address2: '',
    city: '',
    state: '',
    zipCode: '',
    country: 'US',
    addressType: 'residential',
    ignoreSecondaryAddressMismatch: false,
    useSimplifiedCarrierValidation: false,
    includeCandidate: true
  });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const handleInputChange = (field) => (event) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const handleCheckboxChange = (field) => (event) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.checked
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const authToken = getAuthToken();
      console.log('Auth token being used:', authToken ? `${authToken.substring(0, 20)}...` : 'NOT SET');
      if (!authToken) {
        throw new Error('Authentication required. Please log in.');
      }

      const requestBody = {
        address: {
          name: formData.name,
          street1: formData.address1,
          street2: formData.address2 || '',
          city: formData.city,
          state: formData.state,
          postalCode: formData.zipCode,
          countryCode: formData.country,
          addressType: formData.addressType
        },
        ignoreSecondaryAddressMismatch: formData.ignoreSecondaryAddressMismatch,
        useSimplifiedCarrierValidation: formData.useSimplifiedCarrierValidation,
        includeCandidate: formData.includeCandidate
      };

      console.log('Sending request body:', requestBody);

      const response = await apiClient.post('/api/v1/address/validate', requestBody);

      if (response.status !== 200 && response.status !== 201) {
        throw new Error(response.data.message || 'Address validation failed');
      }

      setResult(response.data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const isFormValid = formData.address1 && formData.city && formData.state && formData.zipCode;

  return (
    <Box sx={{ width: '100%', height: '100%', pt: 3, pb: 3, pr: 10 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <LocationOn sx={{ mr: 2, color: 'primary.main' }} />
        <Typography variant="h4">Address Validation</Typography>
      </Box>
      
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Enter Address
              </Typography>
              <Box component="form" onSubmit={handleSubmit}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Name (Optional)"
                      value={formData.name}
                      onChange={handleInputChange('name')}
                      placeholder="John Doe"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Address Line 1"
                      value={formData.address1}
                      onChange={handleInputChange('address1')}
                      required
                      placeholder="123 Main Street"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Address Line 2 (Optional)"
                      value={formData.address2}
                      onChange={handleInputChange('address2')}
                      placeholder="Apt 4B, Suite 200"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="City"
                      value={formData.city}
                      onChange={handleInputChange('city')}
                      required
                      placeholder="New York"
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <TextField
                      fullWidth
                      label="State"
                      value={formData.state}
                      onChange={handleInputChange('state')}
                      required
                      placeholder="NY"
                      inputProps={{ maxLength: 2 }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <TextField
                      fullWidth
                      label="ZIP Code"
                      value={formData.zipCode}
                      onChange={handleInputChange('zipCode')}
                      required
                      placeholder="10001"
                      inputProps={{ maxLength: 10 }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Country"
                      value={formData.country}
                      onChange={handleInputChange('country')}
                      disabled
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <InputLabel>Address Type</InputLabel>
                      <Select
                        value={formData.addressType}
                        label="Address Type"
                        onChange={handleInputChange('addressType')}
                      >
                        <MenuItem value="residential">Residential</MenuItem>
                        <MenuItem value="commercial">Commercial</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  
                  {/* Validation Options */}
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" sx={{ mb: 2, color: 'text.primary', fontWeight: 'bold' }}>
                      Validation Options:
                    </Typography>
                  </Grid>
                  
                  <Grid item xs={12} sm={4}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.ignoreSecondaryAddressMismatch}
                          onChange={handleCheckboxChange('ignoreSecondaryAddressMismatch')}
                          color="primary"
                        />
                      }
                      label="Ignore Secondary Address Mismatch"
                    />
                  </Grid>
                  
                  <Grid item xs={12} sm={4}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.useSimplifiedCarrierValidation}
                          onChange={handleCheckboxChange('useSimplifiedCarrierValidation')}
                          color="primary"
                        />
                      }
                      label="Use Simplified Carrier Validation"
                    />
                  </Grid>
                  
                  <Grid item xs={12} sm={4}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.includeCandidate}
                          onChange={handleCheckboxChange('includeCandidate')}
                          color="primary"
                        />
                      }
                      label="Include Candidate Address"
                    />
                  </Grid>
                  
                  <Grid item xs={12}>
                    <Button
                      type="submit"
                      variant="contained"
                      fullWidth
                      disabled={!isFormValid || loading}
                      startIcon={loading ? <CircularProgress size={20} /> : <LocationOn />}
                    >
                      {loading ? 'Validating...' : 'Validate Address'}
                    </Button>
                  </Grid>
                </Grid>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Validation Result
              </Typography>
              
              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  <ErrorIcon sx={{ mr: 1 }} />
                  {error}
                </Alert>
              )}

                  {result && (
                    <Box>
                      <Alert 
                        severity={result.valid ? "success" : "warning"} 
                        sx={{ mb: 2 }}
                      >
                        {result.valid ? (
                          <>
                            <CheckCircle sx={{ mr: 1 }} />
                            Address is valid
                          </>
                        ) : (
                          <>
                            <ErrorIcon sx={{ mr: 1 }} />
                            Address validation issues found
                          </>
                        )}
                      </Alert>

                  <Typography variant="subtitle1" gutterBottom sx={{ color: 'text.primary', fontWeight: 'bold' }}>
                    Original Address:
                  </Typography>
                  <Box sx={{ mb: 2, p: 2, bgcolor: 'success.50', border: '1px solid', borderColor: 'success.200', borderRadius: 1 }}>
                    <Typography variant="body2" sx={{ color: 'text.primary' }}>
                      {formData.address1}
                      {formData.address2 && <><br />{formData.address2}</>}
                      <br />
                      {formData.city}, {formData.state} {formData.zipCode}
                      <br />
                      {formData.country}
                    </Typography>
                  </Box>

                  {result.candidate && (
                    <>
                      <Divider sx={{ my: 2 }} />
                      <Typography variant="subtitle1" gutterBottom sx={{ color: 'text.primary', fontWeight: 'bold' }}>
                        Suggested Address:
                      </Typography>
                      <Box sx={{ mb: 2, p: 2, bgcolor: 'success.50', border: '1px solid', borderColor: 'success.200', borderRadius: 1 }}>
                        <Typography variant="body2" sx={{ color: 'text.primary' }}>
                          {result.candidate.street1}
                          {result.candidate.street2 && <><br />{result.candidate.street2}</>}
                          <br />
                          {result.candidate.city}, {result.candidate.state} {result.candidate.postalCode}
                          <br />
                          {result.candidate.countryCode}
                        </Typography>
                      </Box>
                    </>
                  )}

                  {result.suggestions && result.suggestions.length > 0 && (
                    <>
                      <Divider sx={{ my: 2 }} />
                      <Typography variant="subtitle1" gutterBottom sx={{ color: 'text.primary', fontWeight: 'bold' }}>
                        Suggestions:
                      </Typography>
                      <Box sx={{ p: 2, bgcolor: 'info.50', border: '1px solid', borderColor: 'info.200', borderRadius: 1 }}>
                        {result.suggestions.map((suggestion, index) => (
                          <Typography key={index} variant="body2" sx={{ mb: 1, color: 'text.primary' }}>
                            • {suggestion}
                          </Typography>
                        ))}
                      </Box>
                    </>
                  )}
                </Box>
              )}

              {!result && !error && (
                <Typography sx={{ color: 'text.primary', fontStyle: 'italic' }}>
                  Enter an address above to validate it.
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AddressValidation;